import { useMemo, useState } from 'react';
import Table from '../../../component/table/table';
import TableFooter from '../../../component/table/tableFooter';
import { useOrganizationContext } from '../../../service/context/organizationContext';
import {
  VenuesViewAddOptions,
  VenuesViewKebabOptions,
  VenuesViewProps,
  VenuesViewSearchParams,
} from './venuesView.type';
import { OrgUnit } from '../../../utils/helper.type';
import { toast } from 'react-toastify';
import i18n from '../../../i18n';
import {
  ActionButtonsVisibility,
  OrganisationToggleEnum,
} from '../userManagement.type';
import { OrganizationContextType } from '../../../service/context/organizationContext.types';
import { OrganizationUnitType, UserType } from '@inplay/models';
import { OrgUnitPresentationFactory } from '../../../strategies/orgUnitPresentation/orgUnitPresentation';
import { OrgUnitPresentation } from '../../../strategies/orgUnitPresentation/orgUnitPresentation.interface';
import { validateAndDecodeAuthToken } from '../../../utils/helper';
import { VenuesViewTableColumns } from '../../../strategies/orgUnitPresentation/orgUnitPresentationTypes';
import {
  addCellContentToTableHeader,
  applyFiltersToOrgUnits,
  getAddMenuOptions,
  filterOrgUnits,
  getViewByOptions,
} from './venuesViewHelper';
import { TableHeader } from '../../../component/table/table.type';
import { useSearchParams } from 'react-router-dom';
import { UserManagementTableHeader } from '../userManagementTableHeader';
import EditOrgUnit from '../editOrgUnit';
import AddOrganisationModal from './addOrganisationModal';
import {
  BaseUserFilter,
  UserManagementFilter,
} from '../../../component/userManagementFilter';
import { AddMenuOption } from '../userManagementHelper';

export const VenuesView = (props: VenuesViewProps) => {
  const context: OrganizationContextType = useOrganizationContext();
  const [selectedOrgUnits, setSelectedOrgUnits] = useState<OrgUnit[]>([]);
  const [venueFilter, setVenueFilter] = useState(BaseUserFilter);

  const [isVenueFilterModalOpen, setIsVenueFilterModalOpen] =
    useState<boolean>(false);
  const [editOrg, setEditOrg] = useState<{
    isOpen: boolean;
    org: OrgUnit | undefined;
    editType: VenuesViewKebabOptions | undefined;
  }>({
    isOpen: false,
    org: undefined,
    editType: undefined,
  });
  const [addOrg, setAddOrg] = useState<{
    type: VenuesViewAddOptions | undefined;
    show: boolean;
  }>({ type: undefined, show: false });
  const [searchParams, setSearchParams] = useSearchParams();

  const nestedLevel: number = parseInt(
    searchParams.get(VenuesViewSearchParams.NESTED_LEVEL) || '0',
  );

  // Get the first selected org unit ID from the search params, which will be a comma-separated string
  const selectedOrgUnitId: string | null =
    searchParams
      .get(VenuesViewSearchParams.SELECTED_ORG_UNIT_IDS)
      ?.split(',')
      ?.at(0) || null;

  const validAuthToken = validateAndDecodeAuthToken();
  const userType = validAuthToken?.type as UserType;
  const presentationService: OrgUnitPresentation<OrgUnit> = useMemo(
    () =>
      OrgUnitPresentationFactory.getPresentationStrategy(
        userType,
        props.selectedOrganisationToggle,
        nestedLevel,
      ) as OrgUnitPresentation<OrgUnit>, // must typecast because the factory function treats the union as if the types will be mixed within each class, but they won't be
    [userType, props.selectedOrganisationToggle, nestedLevel],
  );

  const orgUnits: OrgUnit[] = useMemo(
    () =>
      presentationService.getTableData(
        context.organizationSummary || [],
        selectedOrgUnitId,
      ),
    [context.organizationSummary, presentationService, selectedOrgUnitId],
  );

  const filteredOrgUnits: OrgUnit[] = useMemo(() => {
    const filteredByQuery = applyFiltersToOrgUnits(orgUnits, props.query);
    return filterOrgUnits(filteredByQuery, venueFilter, props.usersList);
  }, [orgUnits, props.query, venueFilter, props.usersList]);

  const tableColumnsWithDataFormat: TableHeader<OrgUnit>[] = useMemo(() => {
    const tableColumns: VenuesViewTableColumns[] =
      presentationService.getTableColumns(
        props.usersList,
        context.organizationSummary || [],
      );

    return addCellContentToTableHeader(
      tableColumns,
      props.usersList,
      context.organizationSummary || [],
    );
  }, [props.usersList, context.organizationSummary, presentationService]);

  const showFunctionalityComingSoonToast = (): void => {
    // use the translation library
    // @ts-ignore translation component type expects no arguments, but it accepts an argument
    toast.info(i18n.t('common.functionalityComingSoon'), {
      autoClose: 3000,
    });
  };

  const handleClickAddButton = (action: VenuesViewAddOptions) => {
    if (
      action === VenuesViewAddOptions.ADD_SUB_ORGANISATION ||
      action === VenuesViewAddOptions.ADD_VENUE ||
      action === VenuesViewAddOptions.ADD_VENUE_BRANCH
    ) {
      setAddOrg({ type: action, show: true });
    } else if (action === VenuesViewAddOptions.ADD_USER) {
      props.handleAddUser(AddMenuOption.ADD_USER);
    } else {
      showFunctionalityComingSoonToast();
    }
  };

  const handleSelectActionFromKebabMenu = async (
    action: string,
    row: OrgUnit,
    rowIndex: number,
  ) => {
    if (
      action === VenuesViewKebabOptions.DELETE_SUB_ORG ||
      action === VenuesViewKebabOptions.DELETE_VENUE ||
      action === VenuesViewKebabOptions.DELETE_VENUE_BRANCH
    ) {
      showFunctionalityComingSoonToast();
    } else {
      setEditOrg({
        isOpen: true,
        org: row,
        editType: action as VenuesViewKebabOptions,
      });
    }
  };

  const tableHeaderButtonVisiblity: ActionButtonsVisibility = {
    isShowActivate: Boolean(selectedOrgUnits.length), // TODO: PC-640 update organization units
    isShowDeactivate: Boolean(selectedOrgUnits.length), // TODO: PC-640 update organization units
    isShowDelete: Boolean(selectedOrgUnits.length), // TODO: PC-640 update organization units
    isShowAdd: !selectedOrgUnits.length && !nestedLevel, // TODO: PC-640 update organization units
  };

  const handleClickToggle = (selectedToggle: OrganisationToggleEnum): void => {
    // clear selected org units when toggle changes
    setSelectedOrgUnits([]);
    setSearchParams();
    props.setSelectedOrganisationToggle(selectedToggle);
  };

  const handleClickRow = (row: OrgUnit): void => {
    setSearchParams((prevParams) => {
      const nestedLevel = prevParams.get(VenuesViewSearchParams.NESTED_LEVEL);
      const newNestedLevel = nestedLevel ? parseInt(nestedLevel) + 1 : 1;

      const prevOrgUnitIds: string[] =
        prevParams
          .get(VenuesViewSearchParams.SELECTED_ORG_UNIT_IDS)
          ?.split(',') || [];

      prevOrgUnitIds.unshift(row._id);

      const updatedParams: Record<string, string> = {
        ...Object.fromEntries(prevParams.entries()),
        [VenuesViewSearchParams.NESTED_LEVEL]: newNestedLevel.toString(),
        [VenuesViewSearchParams.SELECTED_ORG_UNIT_IDS]:
          prevOrgUnitIds.join(','),
      };

      // Conditionally add or remove SHOW_USERS param
      if (
        (props.selectedOrganisationToggle === OrganisationToggleEnum.VENUES &&
          row.type === OrganizationUnitType.VENUE_BRANCH) ||
        (props.selectedOrganisationToggle ===
          OrganisationToggleEnum.ORGANISATION &&
          row.type === OrganizationUnitType.SUB_ORGANIZATION)
      ) {
        updatedParams[VenuesViewSearchParams.SHOW_USERS] = 'true';
      } else {
        delete updatedParams[VenuesViewSearchParams.SHOW_USERS];
      }
      return updatedParams;
    });
  };

  return (
    <div>
      {editOrg.isOpen && (
        <EditOrgUnit
          isOpen={editOrg.isOpen}
          setIsOpen={(value: boolean) => {
            setEditOrg({ isOpen: value, org: undefined, editType: undefined });
          }}
          editType={editOrg.editType}
          data={editOrg.org}
        />
      )}
      {addOrg.show && addOrg.type && (
        <AddOrganisationModal
          show={addOrg.show}
          onToggle={(value: boolean) =>
            setAddOrg({ show: value, type: undefined })
          }
          type={addOrg.type}
        />
      )}
      {/* Renders Table content. */}
      <div className="table-content flex flex-col overflow-hidden">
        <UserManagementTableHeader
          allUsers={[]} // TODO: PC-574: add filter logic
          selectedOrganisationToggle={props.selectedOrganisationToggle}
          setSelectedOrganisationToggle={handleClickToggle}
          handleAddActions={handleClickAddButton}
          addMenuOptions={getAddMenuOptions(
            props.selectedOrganisationToggle,
            presentationService.orgUnitType,
            Boolean(nestedLevel),
            userType,
          )}
          searchQuery={props.query}
          handleOnChangeSearch={props.handleSearchChange}
          selectedCount={selectedOrgUnits.length}
          unitTranslationKey={presentationService.unitTranslationKey}
          handleClose={() => setSelectedOrgUnits([])}
          handleActivate={showFunctionalityComingSoonToast} // TODO: PC-640 update organization units
          handleDeactivate={showFunctionalityComingSoonToast} // TODO: PC-640 update organization units
          handleDelete={showFunctionalityComingSoonToast} // TODO: PC-640 update organization units
          actionVisibility={tableHeaderButtonVisiblity}
        >
          <UserManagementFilter
            selectedOrganisationToggle={props.selectedOrganisationToggle}
            filters={venueFilter}
            setFilters={setVenueFilter}
            setFilterModalOpen={setIsVenueFilterModalOpen}
            isOpenUserModal={isVenueFilterModalOpen}
            viewByOptions={
              getViewByOptions(presentationService.orgUnitType) ?? []
            }
          />
        </UserManagementTableHeader>
        <div className="flex flex-col flex-grow overflow-hidden">
          <div className="flex-grow overflow-y-auto max-h-[700px]">
            <Table
              onSelectionChange={setSelectedOrgUnits}
              tableHeader={tableColumnsWithDataFormat}
              selectedRows={selectedOrgUnits}
              tableData={filteredOrgUnits}
              loading={context.loading}
              onViewDetail={handleClickRow} //TODO: PC-566 navigate to nested view
              onKebabSelect={handleSelectActionFromKebabMenu}
              loadingMenu={{ rowIndex: -1, action: '' }}
              getKebabMenuOptions={presentationService.getKebabOptions}
            />
          </div>
          <div className="sticky bottom-0 bg-white z-10">
            <TableFooter
              footerText={'userManagement.table.footer'}
              displayedCount={filteredOrgUnits.length}
              totalCount={orgUnits.length}
              unitText={presentationService.unitTranslationKey}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
